import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { ThemeProvider } from "@/context/ThemeContext";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import Users from "@/pages/Users";
import Clients from "@/pages/Clients";
import Plans from "@/pages/Plans";
import Subscriptions from "@/pages/Subscriptions";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import Integrations from "@/pages/Integrations";
import RolesPermissions from "@/pages/RolesPermissions";
import Placeholders from "@/pages/Placeholders";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ChangePassword from "@/pages/ChangePassword";
import PublicQuote from "@/pages/PublicQuote";

import CRMClients from "@/pages/CRMClients";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import BillingCenter from "@/pages/BillingCenter";
import ClientPortalBilling from "@/pages/ClientPortalBilling";
import TrustedClients from "@/pages/TrustedClients";
import Testimonials from "@/pages/Testimonials";
import CaseStudies from "@/pages/public/CaseStudies";
import { PrivacyPolicy, TermsOfService, CookiePolicy, PaymentPolicy } from "@/pages/public/Legal";
import BookScheduling from "@/pages/public/BookScheduling";
import Scheduling from "@/pages/Scheduling";
import { PublicHome, PublicServices, PublicPortfolio, PublicPricing, PublicAbout, PublicContact } from "@/pages/public/PublicSite";
import ServiceDetail from "@/pages/public/ServiceDetail";
import LocalSeoLanding from "@/pages/public/seo/LocalSeoLanding";
import LocalSeoIndex from "@/pages/public/seo/LocalSeoIndex";
import { LOCAL_SEO_PAGES, LOCAL_SEO_KEYS } from "@/pages/public/seo/localSeoContent";
import { PortfolioDetail, Blog, BlogPost, Careers, ClientSuccess } from "@/pages/public/PublicExtra";
import Projects from "@/pages/Projects";
import ProjectsKanban from "@/pages/ProjectsKanban";
import Quotes from "@/pages/Quotes";
import Invoices from "@/pages/Invoices";
import Support from "@/pages/Support";
import Tasks from "@/pages/Tasks";
import CalendarPage from "@/pages/CalendarPage";
import Analytics from "@/pages/Analytics";
import AIAssistant from "@/pages/AIAssistant";
import PaymentHistory from "@/pages/PaymentHistory";
import Renewals from "@/pages/Renewals";
import Reports from "@/pages/Reports";
import FileManager from "@/pages/FileManager";
import ActivityLog from "@/pages/ActivityLog";
import {
  Hosting, Domains, SSL, GBP, SEO, Social, NFC, Uniforms, MobileAppsPage, WebsitesPage,
} from "@/pages/GenericModules";
import { FollowUps, Notes, Branding, GraphicDesign, ContentCalendar, Printing } from "@/pages/GenericModulesExtra";
import {
  ClientPortalHome, ClientPortalProjects, ClientPortalInvoices, ClientPortalSupport,
  ClientPortalAI, ClientPortalDomains, ClientPortalHosting, ClientPortalFiles, ClientPortalReports,
} from "@/pages/ClientPortal";

function Protected({ children, portalOnly = false, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <div className="min-h-screen bg-zinc-950 grid place-items-center text-zinc-500 text-sm">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (portalOnly && user.role !== "client") return <Navigate to="/app" replace />;
  if (adminOnly && user.role === "client") return <Navigate to="/portal" replace />;
  return children;
}

function AutoRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen bg-zinc-950 grid place-items-center text-zinc-500 text-sm">Loading…</div>;
  // Marketing site is the default for anonymous visitors.
  if (!user) return <PublicHome />;
  return <Navigate to={user.role === "client" ? "/portal" : "/app"} replace />;
}

function RootRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
      <Route path="/quote/:token" element={<PublicQuote />} />

      {/* Public marketing website (anonymous access) */}
      <Route path="/services" element={<PublicServices />} />
      <Route path="/services/huntsville" element={<LocalSeoIndex lang="en" />} />
      <Route path="/es/servicios" element={<LocalSeoIndex lang="es" />} />
      {LOCAL_SEO_KEYS.map((key) => {
        const en = LOCAL_SEO_PAGES[key].en;
        const es = LOCAL_SEO_PAGES[key].es;
        return (
          <React.Fragment key={key}>
            <Route path={`/services/${en.slug}`} element={<LocalSeoLanding serviceKey={key} lang="en" />} />
            <Route path={`/es/servicios/${es.slug}`} element={<LocalSeoLanding serviceKey={key} lang="es" />} />
          </React.Fragment>
        );
      })}
      <Route path="/services/:key" element={<ServiceDetail />} />
      <Route path="/portfolio" element={<PublicPortfolio />} />
      <Route path="/portfolio/:key" element={<PortfolioDetail />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/success" element={<ClientSuccess />} />
      <Route path="/pricing" element={<PublicPricing />} />
      <Route path="/about" element={<PublicAbout />} />
      <Route path="/contact" element={<PublicContact />} />
      <Route path="/case-studies" element={<CaseStudies />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/payment-policy" element={<PaymentPolicy />} />
      <Route path="/contact/callback" element={<BookScheduling appointmentType="callback" />} />
      <Route path="/contact/strategy-call" element={<BookScheduling appointmentType="strategy_call" />} />

      {/* Admin app */}
      <Route path="/app" element={<Protected adminOnly><Dashboard /></Protected>} />
      <Route path="/app/crm" element={<Protected adminOnly><CRMClients /></Protected>} />
      <Route path="/app/leads" element={<Protected adminOnly><Leads /></Protected>} />
      <Route path="/app/leads/:lead_id" element={<Protected adminOnly><LeadDetail /></Protected>} />
      <Route path="/app/followups" element={<Protected adminOnly><FollowUps /></Protected>} />
      <Route path="/app/notes" element={<Protected adminOnly><Notes /></Protected>} />
      <Route path="/app/projects" element={<Protected adminOnly><Projects /></Protected>} />
      <Route path="/app/projects/kanban" element={<Protected adminOnly><ProjectsKanban /></Protected>} />
      <Route path="/app/projects/:kind" element={<Protected adminOnly><Projects /></Protected>} />
      <Route path="/app/files" element={<Protected adminOnly><FileManager /></Protected>} />
      <Route path="/app/activity" element={<Protected adminOnly><ActivityLog /></Protected>} />
      <Route path="/app/branding" element={<Protected adminOnly><Branding /></Protected>} />
      <Route path="/app/graphic-design" element={<Protected adminOnly><GraphicDesign /></Protected>} />
      <Route path="/app/quotes" element={<Protected adminOnly><Quotes /></Protected>} />
      <Route path="/app/invoices" element={<Protected adminOnly><Invoices /></Protected>} />
      <Route path="/app/payments" element={<Protected adminOnly><PaymentHistory /></Protected>} />
      <Route path="/app/renewals" element={<Protected adminOnly><Renewals /></Protected>} />
      <Route path="/app/billing" element={<Protected adminOnly><BillingCenter /></Protected>} />
      <Route path="/app/trusted-clients" element={<Protected adminOnly><TrustedClients /></Protected>} />
      <Route path="/app/testimonials" element={<Protected adminOnly><Testimonials /></Protected>} />
      <Route path="/app/scheduling" element={<Protected adminOnly><Scheduling /></Protected>} />
      <Route path="/app/content" element={<Protected adminOnly><ContentCalendar /></Protected>} />
      <Route path="/app/printing" element={<Protected adminOnly><Printing /></Protected>} />
      <Route path="/app/reports" element={<Protected adminOnly><Reports /></Protected>} />
      <Route path="/app/support" element={<Protected adminOnly><Support /></Protected>} />
      <Route path="/app/tasks" element={<Protected adminOnly><Tasks /></Protected>} />
      <Route path="/app/calendar" element={<Protected adminOnly><CalendarPage /></Protected>} />
      <Route path="/app/ai" element={<Protected adminOnly><AIAssistant /></Protected>} />
      <Route path="/app/analytics" element={<Protected adminOnly><Analytics /></Protected>} />

      <Route path="/app/hosting" element={<Protected adminOnly><Hosting /></Protected>} />
      <Route path="/app/domains" element={<Protected adminOnly><Domains /></Protected>} />
      <Route path="/app/ssl" element={<Protected adminOnly><SSL /></Protected>} />
      <Route path="/app/gbp" element={<Protected adminOnly><GBP /></Protected>} />
      <Route path="/app/seo" element={<Protected adminOnly><SEO /></Protected>} />
      <Route path="/app/social" element={<Protected adminOnly><Social /></Protected>} />
      <Route path="/app/nfc" element={<Protected adminOnly><NFC /></Protected>} />
      <Route path="/app/uniforms" element={<Protected adminOnly><Uniforms /></Protected>} />
      <Route path="/app/mobile-apps" element={<Protected adminOnly><MobileAppsPage /></Protected>} />
      <Route path="/app/websites" element={<Protected adminOnly><WebsitesPage /></Protected>} />

      <Route path="/app/companies" element={<Protected adminOnly><Companies /></Protected>} />
      <Route path="/app/users" element={<Protected adminOnly><Users /></Protected>} />
      <Route path="/app/clients" element={<Protected adminOnly><Clients /></Protected>} />
      <Route path="/app/plans" element={<Protected adminOnly><Plans /></Protected>} />
      <Route path="/app/subscriptions" element={<Protected adminOnly><Subscriptions /></Protected>} />
      <Route path="/app/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/app/integrations" element={<Protected adminOnly><Integrations /></Protected>} />
      <Route path="/app/roles" element={<Protected adminOnly><RolesPermissions /></Protected>} />
      <Route path="/app/future" element={<Protected adminOnly><Placeholders /></Protected>} />
      <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/app/profile" element={<Protected><Profile /></Protected>} />

      {/* Client portal */}
      <Route path="/portal" element={<Protected><ClientPortalHome /></Protected>} />
      <Route path="/portal/projects" element={<Protected><ClientPortalProjects /></Protected>} />
      <Route path="/portal/invoices" element={<Protected><ClientPortalInvoices /></Protected>} />
      <Route path="/portal/billing" element={<Protected><ClientPortalBilling /></Protected>} />
      <Route path="/portal/support" element={<Protected><ClientPortalSupport /></Protected>} />
      <Route path="/portal/ai" element={<Protected><ClientPortalAI /></Protected>} />
      <Route path="/portal/domains" element={<Protected><ClientPortalDomains /></Protected>} />
      <Route path="/portal/hosting" element={<Protected><ClientPortalHosting /></Protected>} />
      <Route path="/portal/files" element={<Protected><ClientPortalFiles /></Protected>} />
      <Route path="/portal/reports" element={<Protected><ClientPortalReports /></Protected>} />

      <Route path="/" element={<AutoRedirect />} />
      <Route path="*" element={<AutoRedirect />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <LangProvider>
        <BrowserRouter>
          <AuthProvider>
            <div className="App">
              <RootRouter />
              <Toaster theme="dark" position="top-right" richColors duration={1800} toastOptions={{ style: { pointerEvents: "auto" } }} className="!pointer-events-none" />
            </div>
          </AuthProvider>
        </BrowserRouter>
      </LangProvider>
    </ThemeProvider>
  );
}
